import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Behaviour of the FAQ page (sections/faq-page.liquid).
 *
 * Search: the field lives in the hero section above (snippets/faq-search.liquid,
 * id "FaqSearch"), so it is looked up by id rather than as a ref. It stays
 * hidden until this connects. Typing filters the questions, opens the matches,
 * and hides groups with none; the clear button (shown only while there is text)
 * resets it. The match count goes to a visually hidden status region, so screen
 * readers still hear it.
 *
 * Anchors: every question is a <details> whose id is its entry handle. Links
 * such as /pages/faq#faq-does-it-hurt, and the cross-links inside answers,
 * point at a closed <details>; this opens it, on load and on every hash change.
 * The section links in the hero point at a category; if a search is hiding it,
 * the search is cleared first.
 *
 * Contents list: on desktop the categories are listed in a sticky sidebar
 * (sections/faq-page.liquid). While the page scrolls, the category whose heading
 * has passed the upper third of the screen is marked aria-current, at most once
 * per frame; a search hides the links of the categories it hides.
 *
 * Videos: plays the video in a related-video dialog (snippets/faq-resource.liquid)
 * when it opens and pauses it when it closes. Those dialog events don't bubble,
 * so they are caught on the way down.
 *
 * Without this module the whole page still reads top to bottom and links still
 * scroll to the right question.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement[]} [items] - Question disclosures.
 * @property {HTMLElement[]} [categories] - Question groups.
 * @property {HTMLAnchorElement[]} [tocLinks] - Sidebar links, one per group, in the same order.
 * @property {HTMLElement} [status] - Visually hidden live region for result counts.
 * @property {HTMLElement} [empty] - Shown when nothing matches.
 *
 * @extends Component<Refs>
 */
class FaqPageComponent extends Component {
  #controller = new AbortController();

  /** @type {HTMLInputElement | null} */
  #searchInput = null;

  /** Questions opened by a search, closed again when the search is cleared. */
  #openedBySearch = new Set();

  /** Pending animation frame for the contents list, so a scroll marks it once per frame. */
  #currentFrame = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    const search = document.getElementById('FaqSearch');
    const input = document.getElementById('FaqSearchInput');
    if (search && input instanceof HTMLInputElement) {
      search.hidden = false;
      this.#searchInput = input;
      input.addEventListener('input', this.#search, { signal });
      document.getElementById('FaqSearchClear')?.addEventListener('click', this.#clear, { signal });
    }

    window.addEventListener('hashchange', this.#openFromHash, { signal });
    // On desktop the theme scrolls .page-wrapper, not the window (assets/base.css),
    // and element scroll events don't bubble; capturing at the document catches both.
    document.addEventListener('scroll', this.#scheduleMarkCurrent, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#scheduleMarkCurrent, { passive: true, signal });
    this.addEventListener('dialog:open', this.#playVideo, { capture: true, signal });
    this.addEventListener('close', this.#pauseVideo, { capture: true, signal });
    this.#openFromHash();
    this.#markCurrent();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    cancelAnimationFrame(this.#currentFrame);
  }

  #scheduleMarkCurrent = () => {
    if (this.#currentFrame) return;
    this.#currentFrame = requestAnimationFrame(() => {
      this.#currentFrame = 0;
      this.#markCurrent();
    });
  };

  /** Marks the sidebar link of the category being read: the last visible one whose top has passed a third of the screen. */
  #markCurrent() {
    const { categories = [], tocLinks = [] } = this.refs;
    if (!tocLinks.length) return;

    const line = window.innerHeight / 3;
    const visible = categories.filter((category) => !category.hidden);
    let current = visible[0];
    for (const category of visible) {
      if (category.getBoundingClientRect().top > line) break;
      current = category;
    }

    for (const link of tocLinks) {
      if (current && link.hash === `#${current.id}`) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }

  #search = debounce(() => this.#filter(), 150);

  /** Empties the field, shows every question again, and keeps focus in the field. */
  #clear = () => {
    if (!this.#searchInput) return;

    this.#searchInput.value = '';
    this.#filter();
    this.#searchInput.focus();
  };

  #filter() {
    const query = normalize(this.#searchInput?.value ?? '');
    const { items = [], categories = [], status, empty } = this.refs;

    let matches = 0;

    for (const item of items) {
      const visible = !query || normalize(item.textContent ?? '').includes(query);
      item.hidden = !visible;
      if (!query) continue;

      if (visible) {
        matches += 1;
        if (!item.open) {
          item.open = true;
          this.#openedBySearch.add(item);
        }
      }
    }

    if (!query) {
      for (const item of this.#openedBySearch) item.open = false;
      this.#openedBySearch.clear();
    }

    for (const category of categories) {
      category.hidden = !category.querySelector('.faq-item:not([hidden])');
    }

    // The contents list only offers the categories a search leaves visible.
    for (const link of this.refs.tocLinks ?? []) {
      const category = categories.find((section) => `#${section.id}` === link.hash);
      if (link.parentElement) link.parentElement.hidden = Boolean(category?.hidden);
    }
    this.#markCurrent();

    if (empty) empty.hidden = !query || matches > 0;
    if (status) {
      status.textContent =
        query && matches > 0 ? (this.dataset.countTemplate ?? '').replace('%n%', String(matches)) : '';
    }
  }

  /** @param {Event} event */
  #playVideo = (event) => {
    if (!(event.target instanceof Element)) return;

    const video = event.target.querySelector('dialog video');
    if (!(video instanceof HTMLVideoElement)) return;

    // Autoplay can be refused (data saver, browser policy); the controls stay.
    video.play().catch(() => {});
  };

  /** @param {Event} event */
  #pauseVideo = (event) => {
    if (!(event.target instanceof HTMLDialogElement)) return;

    const video = event.target.querySelector('video');
    if (video instanceof HTMLVideoElement) video.pause();
  };

  #openFromHash = () => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;

    const item = this.refs.items?.find((details) => details.id === id);
    if (!item) {
      // A section link (snippets/faq-category-nav.liquid) to a group a search is hiding.
      const category = this.refs.categories?.find((section) => section.id === id);
      if (category?.hidden && this.#searchInput) {
        this.#searchInput.value = '';
        this.#filter();
        category.scrollIntoView({ block: 'start' });
      }
      return;
    }

    // A search may be hiding the linked question; clear it first.
    if (item.hidden && this.#searchInput) {
      this.#searchInput.value = '';
      this.#filter();
    }

    item.open = true;
    item.scrollIntoView({ block: 'start' });
  };
}

/**
 * Lower-cases and strips accents, so "ecran" finds "écran".
 * @param {string} text
 */
function normalize(text) {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

if (!customElements.get('faq-page-component')) {
  customElements.define('faq-page-component', FaqPageComponent);
}
